import React, { useEffect, useState } from 'react';
import { getBlacklist } from '../../../../../lib/api';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Grid } from '@mui/material';
import EnhacedBox from '../../../../../lib/designSystem/Box';
import HeaderWithActions from '../../../../../lib/designSystem/HeaderWithActions';
import CustomButton from '../../../../../lib/designSystem/Button';
import {
  updateBlacklist,
  deleteBlacklist,
  createBlacklist,
} from '../../../../../lib/api';
import CenteredCircularProgress from '../../../../../lib/designSystem/CircularProgress';
import { showAlert } from '../../../../../lib/functions/utils';

interface BlacklistItem {
  id: number;
  businessIdentifier: string;
  businessName: string;
  reason: string;
}

const Blacklist = (props: any) => {
  const { businessId, businessName } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [blacklist, setBlacklist] = useState<BlacklistItem | null>(null);
  const [editingReason, setEditingReason] = useState<boolean>(false);
  const [addingBlacklist, setAddingBlacklist] = useState<boolean>(false);
  const [deletingBlacklist, setDeletingBlacklist] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const dispatch = useDispatch();

  const fetchBlacklist = async () => {
    const blacklist = await getBlacklist(
      dispatch,
      `businessIdentifier=${businessId}`,
    );
    if (blacklist.data.length > 0) {
      setBlacklist(blacklist.data[0]);
      setReason(blacklist.data[0].reason);
    } else {
      setBlacklist(null);
    }
  };

  useEffect(() => {
    fetchBlacklist();
  }, [businessId]);

  const handleUpdateBlacklist = async () => {
    if (!blacklist) return;
    setLoading(true);
    try {
      await updateBlacklist(dispatch, {
        id: blacklist?.id,
        reason: reason,
      });
      setEditingReason(false);
      await fetchBlacklist();
      showAlert(dispatch, 'success', 'Razón actualizada');
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlacklist = async () => {
    if (!blacklist) return;
    setLoading(true);
    try {
      await deleteBlacklist(dispatch, { id: blacklist?.id });
      await fetchBlacklist();
      showAlert(dispatch, 'success', 'Empresa eliminada de la blacklist');
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    } finally {
      setLoading(false);
      setDeletingBlacklist(false);
    }
  };

  const handleAddBlacklist = async () => {
    if (!reason) return;
    setLoading(true);
    try {
      await createBlacklist(dispatch, {
        businessIdentifier: businessId,
        businessName: businessName,
        reason: reason,
      });
      await fetchBlacklist();
      showAlert(dispatch, 'success', 'Empresa agregada a la blacklist');
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    } finally {
      setLoading(false);
      setAddingBlacklist(false);
    }
  };

  return (
    <>
      <HeaderWithActions
        variant="body1"
        title="Blacklist."
        subHeader
        buttons={[
          ...(blacklist
            ? [
                {
                  action: () => setEditingReason(true),
                  color: 'secondary' as const,
                  customButton: (
                    <CustomButton
                      color="secondary"
                      onClick={() => setEditingReason(true)}
                    >
                      Editar razón
                    </CustomButton>
                  ),
                },
              ]
            : []),
          ...(blacklist
            ? [
                {
                  action: () => setDeletingBlacklist(true),
                  color: 'error' as const,
                  customButton: (
                    <CustomButton
                      color="error"
                      onClick={() => setDeletingBlacklist(true)}
                    >
                      Eliminar de la blacklist
                    </CustomButton>
                  ),
                },
              ]
            : [
                {
                  action: () => setAddingBlacklist(true),
                  color: 'error' as const,
                  customButton: (
                    <CustomButton
                      color="primary"
                      onClick={() => setAddingBlacklist(true)}
                    >
                      Agregar a Blacklist
                    </CustomButton>
                  ),
                },
              ]),
        ]}
      />
      <EnhacedBox small>
        <div style={{ padding: '20px 0px' }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Grid container spacing={2}>
                {blacklist && (
                  <>
                    <Grid item xs={12}>
                      <Typography fontWeight={600}>
                        La empresa está en la blacklist.
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography fontWeight={600}>Razón :</Typography>
                    </Grid>
                    <Grid item xs={10}>
                      <Typography>{blacklist.reason}</Typography>
                    </Grid>
                  </>
                )}
                {!blacklist && (
                  <Grid item xs={12}>
                    <Typography fontWeight={600}>
                      La empresa no está en la blacklist.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </div>
      </EnhacedBox>
      <Dialog
        open={addingBlacklist}
        onClose={() => setAddingBlacklist(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
      >
        <DialogTitle id="form-dialog-title">
          Agregar empresa a la blacklist
        </DialogTitle>
        <DialogContent sx={{ minWidth: '500px' }}>
          {loading && (
            <div style={{ minHeight: '70px' }}>
              <CenteredCircularProgress type="dialog" />
            </div>
          )}
          {!loading && (
            <>
              <Typography variant="body1">{businessName}</Typography>
              <TextField
                autoFocus
                margin="dense"
                id="reason"
                label="Razón"
                type="text"
                fullWidth
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setAddingBlacklist(false)}
            color="secondary"
            disabled={loading}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => handleAddBlacklist()}
            color="primary"
            disabled={loading}
          >
            Agregar
          </CustomButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editingReason}
        onClose={() => setEditingReason(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
      >
        <DialogTitle id="form-dialog-title">
          Editar razón de {blacklist?.businessName}
        </DialogTitle>
        <DialogContent>
          {loading && (
            <div style={{ minHeight: '70px' }}>
              <CenteredCircularProgress type="dialog" />
            </div>
          )}
          {!loading && (
            <TextField
              autoFocus
              margin="dense"
              id="newReason"
              label="Razón"
              type="text"
              fullWidth
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
            />
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setEditingReason(false)}
            color="secondary"
            disabled={loading}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => handleUpdateBlacklist()}
            color="primary"
            disabled={loading}
          >
            Guardar
          </CustomButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deletingBlacklist}
        onClose={() => setDeletingBlacklist(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
      >
        <DialogTitle id="form-dialog-title">
          Eliminar empresa de la blacklist
        </DialogTitle>
        <DialogContent>
          {loading && (
            <div style={{ minHeight: '70px' }}>
              <CenteredCircularProgress type="dialog" />
            </div>
          )}
          {!loading && (
            <Typography variant="body1">
              Estás seguro de querer eliminar a {blacklist?.businessName} de la
              blacklist?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setDeletingBlacklist(false)}
            color="secondary"
            disabled={loading}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => handleDeleteBlacklist()}
            color="error"
            disabled={loading}
          >
            Eliminar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Blacklist;
